import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmationPanel from '../confirmationPanel';

export type EmailTemplateFormValues = {
    name: string;
    subject: string;
    body: string;
};

export type EmailTemplateDialogProps = {
    open: boolean;
    initialValues?: EmailTemplateFormValues;
    title?: string;
    submitLabel?: string;
    onSubmit: (values: EmailTemplateFormValues) => void;
    onClose: () => void;
};

const EmailTemplateDialog: React.FC<EmailTemplateDialogProps> = ({
    open,
    initialValues,
    title,
    submitLabel,
    onSubmit,
    onClose,
}) => {
    const { t } = useTranslation();
    const [values, setValues] = useState<EmailTemplateFormValues>({ name: '', subject: '', body: '' });
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);

    const resolvedTitle = title ?? t('coachingNavigator.emailTemplates.dialog.createTitle');
    const resolvedSubmitLabel = submitLabel ?? t('coachingNavigator.emailTemplates.dialog.create');

    const initialFormState = useMemo(() => ({
        name: initialValues?.name || '',
        subject: initialValues?.subject || '',
        body: initialValues?.body || '',
    }), [initialValues]);

    useEffect(() => {
        if (open) {
            setValues({
                name: initialValues?.name || '',
                subject: initialValues?.subject || '',
                body: initialValues?.body || '',
            });
        }
    }, [open, initialValues]);

    const hasChanges = useMemo(() => {
        return (
            values.name !== initialFormState.name ||
            values.subject !== initialFormState.subject ||
            values.body !== initialFormState.body
        );
    }, [values, initialFormState]);

    const handleWarningClose = useCallback(() => {
        if (hasChanges) {
            setWarningDialogOpen(true);
        } else {
            onClose();
        }
    }, [hasChanges, onClose]);

    const handleSubmit = () => {
        if (!values.name.trim() || !values.subject.trim() || !values.body.trim()) return;
        onSubmit({
            name: values.name.trim(),
            subject: values.subject.trim(),
            body: values.body.trim(),
        });
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={handleWarningClose}
            fullWidth
            maxWidth="sm" slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
        >
            <DialogTitle>{resolvedTitle}</DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label={t('coachingNavigator.emailTemplates.form.name')}
                    value={values.name}
                    onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
                    autoFocus
                />
                <TextField
                    label={t('coachingNavigator.emailTemplates.form.subject')}
                    value={values.subject}
                    onChange={(e) => setValues((prev) => ({ ...prev, subject: e.target.value }))}
                />
                <TextField
                    label={t('coachingNavigator.emailTemplates.form.body')}
                    value={values.body}
                    onChange={(e) => setValues((prev) => ({ ...prev, body: e.target.value }))}
                    multiline
                    minRows={6}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={handleWarningClose}>
                    {t('button.cancel')}
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!values.name.trim() || !values.subject.trim() || !values.body.trim()}>
                    {resolvedSubmitLabel}
                </Button>
            </DialogActions>
        </Dialog>

        <ConfirmationPanel
            action="warning"
            showModal={warningDialogOpen}
            hideModal={() => setWarningDialogOpen(false)}
            confirmModal={() => {
                setWarningDialogOpen(false);
                setValues({ name: initialFormState.name, subject: initialFormState.subject, body: initialFormState.body });
                onClose();
            }}
        />
        </>
    );
};

export default EmailTemplateDialog;
